import { MetaMaskSDK } from '@metamask/sdk'
import { login } from '@/api/user'
import { showNotice, getLoginErrorMessage } from '@/utils/tool'

// 官方 MetaMask SDK 全站只建立一次，跨元件共用。
// 連接／QR 掃碼／安裝引導／手機 deeplink 皆由官方彈窗處理，我方不自建。
let sdkInstance = null
let sdkInitPromise = null

// dappMetadata 只影響官方彈窗顯示的站名／logo／網址，屬裝飾性。
function buildDappMetadata() {
  const origin = window.location.origin
  return {
    name: (typeof document !== 'undefined' && document.title) || window.location.host,
    url: origin,
    iconUrl: origin + '/favicon.ico'
  }
}

// 惰性初始化 SDK 並取得 provider；失敗時清掉 promise 以便下次重試。
function ensureMetaMaskProvider() {
  if (!sdkInstance) {
    sdkInstance = new MetaMaskSDK({
      dappMetadata: buildDappMetadata(),
      // 不在載入當下就檢查安裝，改為使用者點按鈕時才觸發官方彈窗
      checkInstallationImmediately: false,
      logging: { developerMode: false }
    })
  }
  if (!sdkInitPromise) {
    sdkInitPromise = Promise.resolve(sdkInstance.init()).catch(err => {
      sdkInitPromise = null
      throw err
    })
  }
  return sdkInitPromise.then(() => sdkInstance.getProvider())
}

// EIP-1193 使用者拒絕連接／簽章的代碼
function isUserRejected(error) {
  if (!error) {
    return false
  }
  return error.code === 4001 || error.code === 'ACTION_REJECTED'
}

export default {
  computed: {
    showMetaMaskLogin() {
      return this.$store.getters.permissionInfo.enable_metamask_login === 1
    }
  },
  methods: {
    // 每次登入都產生全新的一次性 128-bit nonce（16 bytes → 32 hex）
    createMetaMaskNonce() {
      const bytes = crypto.getRandomValues(new Uint8Array(16))
      return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
    },
    // 依 ERC-4361（SIWE）組成登入訊息；效期 5 分鐘。
    // 簽名前顯示的原文與送後端驗證的原文必須逐字相同，不可自行替換任何欄位。
    // 換行一律用 CRLF（\r\n）：表單序列化送出時會把 LF 轉成 CRLF，若以 LF 簽署，
    // 後端收到的會是 CRLF，還原出的地址不符而回 2306。統一以 CRLF 組字，
    // 讓「簽署的 bytes」與「送出的 bytes」完全一致。
    buildSiweMessage(address, chainId, nonce) {
      const issuedAt = new Date().toISOString()
      const expirationTime = new Date(Date.now() + 5 * 60 * 1000).toISOString()
      const CRLF = '\r\n'
      return [
        `${window.location.host} wants you to sign in with your Ethereum account:`,
        address,
        '',
        'Sign in with MetaMask.',
        '',
        `URI: ${window.location.origin}`,
        'Version: 1',
        `Chain ID: ${chainId}`,
        `Nonce: ${nonce}`,
        `Issued At: ${issuedAt}`,
        `Expiration Time: ${expirationTime}`
      ].join(CRLF)
    },
    buildMetaMaskLoginPayload(address, message, signature) {
      const payload = {
        social_type: 'metamask',
        address,
        message,
        signature
      }
      // 推薦碼／追蹤碼沿用 Google SSO 規則
      const promotionCode = this.$store.getters.shareCode || this.$store.getters.inviteCode ||
        localStorage.getItem('shareCode') || localStorage.getItem('inviteCode')
      if (promotionCode) {
        payload.promotion_code = promotionCode
      }
      const btag = this.$store.getters.btag || localStorage.getItem('btag')
      if (btag) {
        payload.btag = btag
      }
      return payload
    },
    // 入口按鈕點擊：連錢包 → 取鏈 → 組 message → 簽章 → 只呼叫一次 /user/login
    async loginWithMetaMask() {
      if (this.actionLoading) {
        return
      }
      this.actionLoading = true
      try {
        const provider = await ensureMetaMaskProvider()
        if (!provider) {
          // 官方 SDK 仍無法取得 provider（例如環境不支援）→ 提示安裝
          showNotice(this.$t('Bao.metamask_not_detected'))
          return
        }
        const accounts = await provider.request({ method: 'eth_requestAccounts', params: [] })
        const address = accounts && accounts[0]
        if (!address) {
          return
        }
        const chainIdHex = await provider.request({ method: 'eth_chainId', params: [] })
        const chainId = Number.parseInt(chainIdHex, 16)
        const nonce = this.createMetaMaskNonce()
        const message = this.buildSiweMessage(address, chainId, nonce)
        const signature = await provider.request({
          method: 'personal_sign',
          params: [message, address]
        })
        const res = await login(this.buildMetaMaskLoginPayload(address, message, signature))
        this.handleMetaMaskLoginResult(res)
      } catch (error) {
        this.handleMetaMaskError(error)
      } finally {
        this.actionLoading = false
      }
    },
    handleMetaMaskLoginResult(res) {
      const data = res.data.data
      if (data.code === 0) { // 登入成功（未綁定帳號時後端自動建立會員）
        this.$store.commit('SET_SOCIAL_TYPE', data.social_type || 'metamask')
        this.onGoogleLoginSuccess(res) // 沿用第三方登入成功後的共用流程（is_new_account 導轉／保存 token）
      } else if (data.code === 2201) { // 首次錢包登入，需補設 username
        this.$store.commit('SET_SOCIAL_TYPE', data.social_type || 'metamask')
        // MetaMask 無 credential（nonce/signature 一次性不可重用），只暫存 2201 token；
        // setUsernameModal 依 socialType=metamask 走「只帶 token」的 relogin
        this.openSetUsername(data.token, null)
      } else {
        // 2301 登入未開通、2302~2307 系統類皆由 getLoginErrorMessage 對應多語系文案
        showNotice(getLoginErrorMessage(data.code) || res.data.msg)
      }
    },
    handleMetaMaskError(error) {
      if (isUserRejected(error)) {
        // 玩家在 MetaMask 取消連接或簽章；不自動重試、不重複彈窗
        showNotice(this.$t('Bao.metamask_sign_canceled'))
        return
      }
      // /user/login 驗證失敗：格式同一般登入，code 位於 error.response.data.data.code
      if (error && error.response) {
        const errorData = error.response.data.data
        const errorCode = errorData && errorData.code ? errorData.code : ''
        showNotice(getLoginErrorMessage(errorCode) || error.response.data.msg)
        return
      }
      // 其餘（SDK 初始化失敗、找不到 provider 等）視為未偵測到 MetaMask
      console.log(error)
      showNotice(this.$t('Bao.metamask_not_detected'))
    }
  }
}
